import React from 'react';
import { useIntl } from 'react-intl';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import SectionGuard from '../common/SectionGuard';

const GlobalPartnerSection = ({
  stats = [],
  block2 = null,
  revenueDistribution = [],
  title,
  description,
  apiUrl = ''
}) => {
  const intl = useIntl();

  const displayStats = Array.isArray(stats) ? stats : [];
  const displayTitle = title || block2?.title || '';
  const displayDescription = description || block2?.description || '';

  const hasContent = Boolean(
    displayTitle || displayDescription || (displayStats && displayStats.length)
  );

  const { ref, inView } = useInView({
    threshold: 0.25,
    triggerOnce: true
  });

  return (
    <SectionGuard hasContent={hasContent} emptyMessageId="empty.section">
      <section ref={ref} className="relative py-20 md:py-28 overflow-hidden bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 lg:mb-20">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-[#16A34A]" />
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                  Expertise
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-[#0a1628] leading-[1.1] tracking-tight">
                {displayTitle}
              </h2>
            </div>
            {displayDescription ? (
              <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                <p className="text-base md:text-lg text-slate-600 leading-relaxed border-l-2 border-[#16A34A]/40 pl-5">
                  {displayDescription}
                </p>
              </div>
            ) : null}
          </div>

          {displayStats.length > 0 && (
            <div>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 mb-10">
                {intl.formatMessage({ id: 'global.partner.revenueTitle' })}
              </h3>

              <div className="bg-[#0a1628]">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y lg:divide-y-0 divide-white/10">
                  {displayStats.map((stat) => {
                    const value = Number(stat.value) || 0;
                    const pct = Math.max(0, Math.min(100, value));

                    return (
                      <div key={stat.id} className="px-5 py-8 md:px-6 md:py-10">
                        <div className="text-3xl xl:text-4xl font-bold text-white tracking-tight tabular-nums">
                          {inView ? (
                            <CountUp end={value} duration={2.2} separator="," />
                          ) : (
                            '0'
                          )}
                          <span className="text-[#16A34A] ml-0.5">%</span>
                        </div>

                        <div className="mt-5 h-px w-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-[#16A34A] transition-all duration-1000 ease-out"
                            style={{ width: inView ? `${pct}%` : '0%' }}
                          />
                        </div>

                        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 mt-4 leading-snug">
                          {stat.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </SectionGuard>
  );
};

export default GlobalPartnerSection;
